import { useForm, UseFormReturn } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { z } from 'zod'
import useQueryGetAllPermission from '../../api/hooks/useQueryGetAllPermission'
import { Input } from '@/common/ui/input'
import { Checkbox } from '@/common/ui/checkbox'
import { permissionMapper } from '../../mappers'
import { OperatorGroupPermission } from '@/schema'
import { RequiredIndicator } from '@/common/ui/form/required-indicator'

const formSchema = z.object({
	name: z.string().min(1, 'กรุณากรอกชื่อกลุ่ม'),
	writePermission: z.array(z.nativeEnum(OperatorGroupPermission)),
	readPermission: z.array(z.nativeEnum(OperatorGroupPermission)),
})

export type FormValues = z.infer<typeof formSchema>

// Create a shared base form component
export function GroupForm({
	defaultValues,
	onSubmit,
	submitText,
	formRef,
	isDisabled = false,
	hideButtons = false,
	hideNameField = false,
}: {
	defaultValues?: Partial<FormValues>
	onSubmit: (values: FormValues, form: UseFormReturn<FormValues>) => void
	submitText: string
	formRef?: React.MutableRefObject<UseFormReturn<FormValues> | null>
	isDisabled?: boolean
	hideButtons?: boolean
	hideNameField?: boolean
}) {
	const { data: permissions } = useQueryGetAllPermission()
	const navigate = useNavigate()

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			name: defaultValues?.name || '',
			writePermission: defaultValues?.writePermission || [],
			readPermission: defaultValues?.readPermission || [],
		},
	})

	if (formRef) formRef.current = form

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit((values) => onSubmit(values, form))}
				className="space-y-8"
			>
				{!hideNameField && (
					<FormField
						control={form.control}
						name="name"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									<RequiredIndicator /> ชื่อกลุ่ม
								</FormLabel>
								<FormControl>
									<Input
										placeholder="กรอกชื่อของกลุ่ม"
										{...field}
										disabled={isDisabled}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				)}

				<div className="space-y-4">
					<h3 className="text-lg font-medium text-red-500">
						การกำหนดสิทธิ์การใช้งาน
					</h3>
					<div className="grid grid-cols-2 gap-8">
						{/* Write Permissions */}
						<div className="space-y-4">
							<h4 className="font-medium">สิทธิ์การแก้ไข</h4>
							{permissions?.map((permission) => (
								<FormField
									key={`write-${permission}`}
									control={form.control}
									name="writePermission"
									render={({ field }) => (
										<FormItem className="flex flex-row items-center space-x-3 space-y-0 text-center">
											<FormControl>
												<Checkbox
													checked={field.value?.includes(permission)}
													onCheckedChange={(checked) => {
														if (!isDisabled) {
															if (checked) {
																// When write permission is checked, also add read permission
																field.onChange([...field.value, permission])
																const readField =
																	form.getValues('readPermission')
																if (!readField.includes(permission)) {
																	form.setValue('readPermission', [
																		...readField,
																		permission,
																	])
																}
															} else {
																// When write permission is unchecked, only remove write permission
																field.onChange(
																	field.value?.filter(
																		(value) => value !== permission,
																	),
																)
															}
														}
													}}
													disabled={isDisabled}
												/>
											</FormControl>
											<FormLabel className="font-normal">
												{permissionMapper[permission] || permission}
											</FormLabel>
										</FormItem>
									)}
								/>
							))}
						</div>

						{/* Read Permissions */}
						<div className="space-y-4">
							<h4 className="font-medium">สิทธิ์การดู</h4>
							{permissions?.map((permission) => (
								<FormField
									key={`read-${permission}`}
									control={form.control}
									name="readPermission"
									render={({ field }) => (
										<FormItem className="flex flex-row items-center space-x-3 space-y-0">
											<FormControl>
												<Checkbox
													checked={field.value?.includes(permission)}
													onCheckedChange={(checked) => {
														if (!isDisabled) {
															if (checked) {
																field.onChange([...field.value, permission])
															} else {
																// When read permission is unchecked, also remove write permission
																field.onChange(
																	field.value?.filter(
																		(value) => value !== permission,
																	),
																)
																const writeField =
																	form.getValues('writePermission')
																if (writeField.includes(permission)) {
																	form.setValue(
																		'writePermission',
																		writeField.filter(
																			(value) => value !== permission,
																		),
																	)
																}
															}
														}
													}}
													disabled={isDisabled}
												/>
											</FormControl>
											<FormLabel className="font-normal">
												{permissionMapper[permission] || permission}
											</FormLabel>
										</FormItem>
									)}
								/>
							))}
						</div>
					</div>
				</div>

				{!hideButtons && (
					<>
						<Button type="submit" variant="default" className="mr-4">
							{submitText}
						</Button>
						<Button
							type="button"
							variant="outline"
							onClick={() => navigate(-1)}
						>
							ยกเลิก
						</Button>
					</>
				)}
			</form>
		</Form>
	)
}
